import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

// ─── Icons (lifted from the OTC prototype) ──────────────────────────────────

/** Button diameters, per the prototype: voice is the larger, flatter circle. */
export const VOICE_BUTTON_SIZE = 124;
export const VIDEO_BUTTON_SIZE = 84;

export function MicIcon({ size = 34 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='#256956'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <rect x='9' y='2' width='6' height='12' rx='3' />
      <path d='M5 10v1a7 7 0 0 0 14 0v-1M12 18v4' />
    </svg>
  );
}

/** The prototype's record/stop mark — a green rounded square. */
export function RecordSquare({ size = 26 }: { size?: number }) {
  return (
    <span
      aria-hidden='true'
      className='block rounded-full bg-[#256956]'
      style={{ width: size, height: size }}
    />
  );
}

export function PlayIcon({ size = 20, fill = '#256956' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill={fill}
      aria-hidden='true'
      className='ml-[3px]'
    >
      <path d='M7 4.8v14.4L19.5 12z' />
    </svg>
  );
}

export function PauseIcon({ size = 20, fill = '#256956' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill={fill}
      aria-hidden='true'
    >
      <rect x='6' y='4' width='4.5' height='16' rx='1.4' />
      <rect x='13.5' y='4' width='4.5' height='16' rx='1.4' />
    </svg>
  );
}

function ReRecordIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M3 12a9 9 0 1 0 2.6-6.4' />
      <path d='M3 4.5V10h5.5' />
    </svg>
  );
}

function TrashIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='#9f1239'
      strokeWidth='1.9'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M4 7h16M9.5 7V5.2A1.2 1.2 0 0 1 10.7 4h2.6a1.2 1.2 0 0 1 1.2 1.2V7' />
      <path d='M6.4 7l.8 12a1.6 1.6 0 0 0 1.6 1.5h6.4a1.6 1.6 0 0 0 1.6-1.5l.8-12' />
      <path d='M10.4 11v6M13.6 11v6' />
    </svg>
  );
}

// ─── Controls ───────────────────────────────────────────────────────────────

type RecordButtonProps = {
  size: number;
  onClick: () => void;
  label: string;
  /** Inset ring the prototype gives the (smaller) video button. */
  ring?: boolean;
  disabled?: boolean;
  children: ReactNode;
  testId?: string;
};

export function RecordButton({
  size,
  onClick,
  label,
  ring = false,
  disabled = false,
  children,
  testId,
}: RecordButtonProps) {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={label}
      disabled={disabled}
      data-testid={testId}
      style={{ width: size, height: size }}
      className={cn(
        'relative flex shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-[#eceae3]',
        'focus-visible:ring-[3px] focus-visible:ring-[#66bda4]/60 focus-visible:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-60',
        ring && 'shadow-[inset_0_0_0_8px_#f6f4ee]',
      )}
    >
      {children}
    </button>
  );
}

/** Idle stack: the button, its two labels and (optionally) the ideas link. */
export function RecorderIdle({
  label,
  sublabel,
  footer,
  children,
  className,
}: {
  label: string;
  sublabel: string;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center', className)}>
      {children}
      <p
        data-testid='recorder-idle-label'
        className='mt-3.5 text-[11.5px] font-bold tracking-[0.07em] text-[#7a847d]'
      >
        {label}
      </p>
      <p className='mt-0.5 text-[11.5px] text-[#9aa49d]'>{sublabel}</p>
      {footer}
    </div>
  );
}

export type TransportAction = 'start' | 'stop' | 'play' | 'pause';

const TRANSPORT_LABELS: Record<TransportAction, string> = {
  start: 'Start',
  stop: 'Stop',
  play: 'Play',
  pause: 'Pause',
};

export function RecorderTransport({
  action,
  onClick,
  testId,
  className,
}: {
  action: TransportAction;
  onClick: () => void;
  testId?: string;
  className?: string;
}) {
  const mark = {
    start: (
      <span aria-hidden='true' className='block size-2.5 rounded-full bg-white' />
    ),
    stop: (
      <span aria-hidden='true' className='block size-2.5 rounded-[3px] bg-white' />
    ),
    play: <PlayIcon size={16} fill='#ffffff' />,
    pause: <PauseIcon size={16} fill='#ffffff' />,
  }[action];

  return (
    <button
      type='button'
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-full border-none bg-[#256956] px-6 text-[13.5px] font-bold text-white transition-opacity',
        'hover:opacity-90 focus-visible:ring-[3px] focus-visible:ring-[#66bda4]/60 focus-visible:outline-none',
        className,
      )}
    >
      {mark}
      {TRANSPORT_LABELS[action]}
    </button>
  );
}

export function RecordAgainButton({
  onClick,
  className,
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      data-testid='recorder-record-again'
      className={cn(
        'inline-flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-[12.5px] font-bold text-[#256956]',
        className,
      )}
    >
      <ReRecordIcon />
      Record again
    </button>
  );
}

export function DeleteRecordingButton({
  onClick,
  variant = 'boxed',
}: {
  onClick: () => void;
  variant?: 'boxed' | 'bare';
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label='Delete recording'
      title='Delete recording'
      data-testid='recorder-delete'
      className={cn(
        'flex shrink-0 cursor-pointer items-center justify-center p-0',
        variant === 'boxed'
          ? 'size-[46px] rounded-[14px] border-[1.5px] border-[#e8e4de] bg-white'
          : 'size-11 rounded-xl border-none bg-transparent',
      )}
    >
      <TrashIcon size={variant === 'boxed' ? 18 : 19} />
    </button>
  );
}

/**
 * Permission / capability failure. Sits under the idle button so the pane
 * stays usable and the user can grant access and tap again (AC 8).
 */
export function RecorderNotice({ children }: { children: ReactNode }) {
  return (
    <p
      role='alert'
      data-testid='recorder-notice'
      className='mt-4 rounded-[13px] border border-[#fecdd3] bg-[#fff1f2] px-3.5 py-3 text-center text-[12.5px] leading-[1.45] text-[#9f1239]'
    >
      {children}
    </p>
  );
}
